// src/components/AddTransactionModal.js
import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';

const AddTransactionModal = ({ onClose, currentDate, onSave }) => {
    const formRef = useRef(null);
    const descricaoRef = useRef(null);

    const [formData, setFormData] = useState({
        descricao: '',
        valor: '',
        data: currentDate || format(new Date(), 'yyyy-MM-dd'),
        forma_pagamento: 'Dinheiro',
        tipo: 'entrada', // Default para 'entrada' para corresponder ao ENUM do banco
        cliente: '',
        observacoes: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Foco automático e configurar listeners de teclado
    useEffect(() => {
        descricaoRef.current?.focus();

        const handleKeyDown = (e) => {
            // Tecla Enter - submete o formulário
            if (e.key === 'Enter') {
                e.preventDefault();
                const submitButton = formRef.current?.querySelector('button[type="submit"]');
                submitButton?.click();
            }

            // Atalhos F1-F5 para forma de pagamento
            if (e.key === 'F1' || e.key === 'F2' || e.key === 'F3' || e.key === 'F4' || e.key === 'F5') {
                e.preventDefault();
                const paymentMethods = {
                    'F1': 'Dinheiro',
                    'F2': 'Cartão de Crédito',
                    'F3': 'Cartão de Débito',
                    'F4': 'PIX',
                    'F5': 'Transferência'
                };
                const selectedMethod = paymentMethods[e.key];
                setFormData(prev => ({ ...prev, forma_pagamento: selectedMethod }));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Capitaliza a primeira letra da descrição
    const handleDescricaoChange = (e) => {
        const value = e.target.value;
        const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
        setFormData(prev => ({ ...prev, descricao: capitalized }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleValorChange = (e) => {
        const rawValue = e.target.value.replace(/\D/g, '');
        const formattedValue = rawValue
            ? (Number(rawValue) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
            : '';
        setFormData(prev => ({ ...prev, valor: formattedValue }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            const valorNumerico = parseFloat(
                formData.valor.replace('.', '').replace(',', '.')
            );

            await onSave({
                ...formData,
                valor: valorNumerico
            });

            onClose();
        } catch (err) {
            setError('Erro ao salvar transação. Tente novamente.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={modalContentStyle}> {/* Estilo aplicado aqui */}
                <h2 style={modalTitleStyle}>Adicionar Transação</h2>

                {error && (
                    <div style={{
                        color: '#721c24',
                        backgroundColor: '#f8d7da',
                        padding: '8px', /* Padding menor */
                        borderRadius: '4px',
                        marginBottom: '10px', /* Margem menor */
                        fontSize: '13px' /* Fonte menor */
                    }}>
                        {error}
                    </div>
                )}

                <form ref={formRef} onSubmit={handleSubmit}>
                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label style={labelStyle}>Descrição *</label>
                        <input
                            ref={descricaoRef}
                            type="text"
                            name="descricao"
                            value={formData.descricao}
                            onChange={handleDescricaoChange}
                            disabled={isSubmitting}
                            required
                            style={inputStyle} /* Estilo aplicado aqui */
                        />
                    </div>

                    <div className="form-row" style={formRowStyle}> {/* Estilo aplicado aqui */}
                        <div className="form-group" style={{ flex: 1 }}>
                            <label style={labelStyle}>Valor *</label>
                            <input
                                type="text"
                                name="valor"
                                value={formData.valor}
                                onChange={handleValorChange}
                                placeholder="0,00"
                                disabled={isSubmitting}
                                required
                                style={inputStyle} /* Estilo aplicado aqui */
                            />
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                            <label style={labelStyle}>Data *</label>
                            <input
                                type="date"
                                name="data"
                                value={formData.data}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                style={inputStyle} /* Estilo aplicado aqui */
                            />
                        </div>
                    </div>

                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label style={labelStyle}>Tipo de Transação *</label>
                        <select
                            name="tipo"
                            value={formData.tipo}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            required
                            style={inputStyle} /* Estilo aplicado aqui */
                        >
                            <option value="entrada">Entrada</option>
                            <option value="saida">Saída</option>
                        </select>
                    </div>

                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label style={labelStyle}>Forma de Pagamento *</label>
                        <select
                            name="forma_pagamento"
                            value={formData.forma_pagamento}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            required
                            style={inputStyle} /* Estilo aplicado aqui */
                        >
                            <option value="Dinheiro">F1 - Dinheiro</option>
                            <option value="Cartão de Crédito">F2 - Cartão de Crédito</option>
                            <option value="Cartão de Débito">F3 - Cartão de Débito</option>
                            <option value="PIX">F4 - PIX</option>
                            <option value="Transferência">F5 - Transferência</option>
                        </select>
                    </div>

                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label style={labelStyle}>Cliente (Opcional)</label>
                        <input
                            type="text"
                            name="cliente"
                            value={formData.cliente}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={inputStyle} /* Estilo aplicado aqui */
                        />
                    </div>

                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label style={labelStyle}>Observações (Opcional)</label>
                        <input
                            type="text"
                            name="observacoes"
                            value={formData.observacoes}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={inputStyle} /* Estilo aplicado aqui */
                        />
                    </div>

                    <div style={buttonGroupStyle}> {/* Estilo aplicado aqui */}
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={cancelButtonStyle} /* Estilo aplicado aqui */
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                            style={saveButtonStyle} /* Estilo aplicado aqui */
                        >
                            {isSubmitting ? 'Salvando...' : 'Salvar (Enter)'}
                        </button>
                    </div>
                </form>

                <div style={shortcutsInfoStyle}> {/* Estilo aplicado aqui */}
                    <p style={{ margin: '0' }}><strong>Atalhos:</strong></p>
                    <p style={{ margin: '0' }}>F1 - Dinheiro | F2 - Cartão Crédito | F3 - Cartão Débito</p>
                    <p style={{ margin: '0' }}>F4 - PIX | F5 - Transferência | Enter - Salvar</p>
                </div>
            </div>
        </div>
    );
};

// --- ESTILOS COMPACTADOS PARA O MODAL ---
const modalContentStyle = {
    padding: '20px', // Padding geral do modal
    borderRadius: '8px',
    width: '100%',
    maxWidth: '450px', // Largura máxima menor
};

const modalTitleStyle = {
    fontSize: '20px', // Título do modal
    marginBottom: '15px'
};

const formGroupStyle = {
    marginBottom: '12px', // Espaçamento entre os grupos de formulário
};

const formRowStyle = {
    display: 'flex',
    gap: '12px', // Espaçamento entre campos na mesma linha
    marginBottom: '12px',
};

const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: '#555',
    fontSize: '13px', // Fonte do label menor
};

const inputStyle = {
    width: '100%',
    padding: '8px 10px', // Padding menor nos inputs
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px', // Fonte do input menor
};

const buttonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // Espaçamento entre os botões
    marginTop: '15px', // Margem superior
};

const baseButtonStyle = {
    padding: '8px 15px', // Padding dos botões
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px', // Fonte dos botões
};

const cancelButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: '#e74c3c',
    color: 'white',
};

const saveButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: '#2ecc71',
    color: 'white',
};

const shortcutsInfoStyle = {
    marginTop: '10px',
    padding: '8px', /* Padding menor */
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    fontSize: '12px' /* Fonte menor */
};

export default AddTransactionModal;